import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { useState, useEffect, useCallback } from 'react'
import { Link } from 'wouter'
import { FileText, Eye, MessageSquare, Calendar, DollarSign, Search, Filter, Trash2, ArrowLeft, ArrowRight } from 'lucide-react'

import toast from 'react-hot-toast'
import { buildApiUrl } from '@/config/api'

interface WatchlistItem {
  id: string
  rfp: {
    id: string
    title: string
    description: string
    requirements: string[]
    budgetMin: number
    budgetMax: number
    budgetCurrency: string
    deadline: string
    category: string
    status: string
    views: number
    responses: number
    createdAt: string
    user: {
      id: string
      name: string
      company: string
      role: string
    }
  }
  addedAt: string
}

interface Pagination {
  page: number
  limit: number
  total: number
  totalPages: number
  hasNext: boolean
  hasPrev: boolean
}

export function Watchlist() {

  const [watchlistItems, setWatchlistItems] = useState<WatchlistItem[]>([])
  const [pagination, setPagination] = useState<Pagination | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')
  const [categoryFilter, setCategoryFilter] = useState('all')
  const [currentPage, setCurrentPage] = useState(1)
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('')

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm)
    }, 300)

    return () => clearTimeout(timer)
  }, [searchTerm])

  const fetchWatchlist = useCallback(async (page = 1) => {
    try {
      setIsLoading(true)
      const token = localStorage.getItem('token')
      if (!token) {
        toast.error('Please log in to view your watchlist')
        return
      }

      const params = new URLSearchParams({
        page: page.toString(),
        limit: '10',
      })

      if (debouncedSearchTerm) params.append('search', debouncedSearchTerm)
      if (statusFilter !== 'all') params.append('status', statusFilter)
      if (categoryFilter !== 'all') params.append('category', categoryFilter)

      const response = await fetch(buildApiUrl(`/api/watchlist?${params}`), {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      })

      if (response.ok) {
        const data = await response.json()
        console.log('Watchlist API response:', data) // Debug log
        if (data.success) {
          setWatchlistItems(data.data)
          setPagination(data.pagination)
        } else {
          console.error('API returned success: false:', data)
          toast.error(data.error || 'Unable to load watchlist. Please try again.')
        }
      } else if (response.status === 401) {
        toast.error('Please log in to view your watchlist')
      } else {
        const errorData = await response.json().catch(() => ({ error: 'Network error' }))
        console.error('API error response:', errorData)
        toast.error(errorData.error || 'Unable to load watchlist. Please check your connection and try again.')
      }
    } catch (error) {
      console.error('Error fetching watchlist:', error)
      toast.error('Unable to load watchlist. Please check your connection and try again.')
    } finally {
      setIsLoading(false)
    }
  }, [debouncedSearchTerm, statusFilter, categoryFilter])

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchWatchlist(currentPage)
    }, 100) // Small delay to prevent rapid successive calls

    return () => clearTimeout(timer)
  }, [currentPage, debouncedSearchTerm, statusFilter, categoryFilter]) // Remove fetchWatchlist from dependencies

  const removeFromWatchlist = async (rfpId: string) => {
    try {
      const token = localStorage.getItem('token')
      if (!token) {
        toast.error('Please log in to view your watchlist')
        return
      }

      const response = await fetch(buildApiUrl(`/api/watchlist/${rfpId}`), {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      })

      if (response.ok) {
        // Update local state instead of making another API call
        setWatchlistItems(prev => {
          const newItems = prev.filter(item => item.rfp.id !== rfpId)
          
          // Update pagination if needed
          if (newItems.length === 0 && currentPage > 1) {
            setCurrentPage(prev => prev - 1)
          }
          
          return newItems
        })
        
        // Update pagination total
        setPagination(prev => prev ? {
          ...prev,
          total: prev.total - 1,
          totalPages: Math.ceil((prev.total - 1) / prev.limit)
        } : null)
        
        toast.success('RFP removed from watchlist')
      } else {
        const errorData = await response.json()
        toast.error(errorData.error || 'Failed to remove from watchlist')
      }
    } catch (error) {
      console.error('Error removing from watchlist:', error)
      toast.error('Failed to remove from watchlist')
    }
  }

  const handleSearch = (value: string) => {
    setSearchTerm(value)
    setCurrentPage(1)
  }

  const handleStatusFilter = (value: string) => {
    setStatusFilter(value)
    setCurrentPage(1)
  }

  const handleCategoryFilter = (value: string) => {
    setCategoryFilter(value)
    setCurrentPage(1)
  }

  const clearFilters = () => {
    setSearchTerm('')
    setDebouncedSearchTerm('')
    setStatusFilter('all')
    setCategoryFilter('all')
    setCurrentPage(1)
  }

  const formatCurrency = (amount: number, currency: string) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount)
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    })
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'PUBLISHED':
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
      case 'DRAFT':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
      case 'CLOSED':
        return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
      case 'AWARDED':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200'
    }
  }

  if (isLoading) {
    return (
      <div className="space-y-8">
        <div className="flex items-center space-x-4">
          <Link href="/request-for-proposal-marketplace">
            <Button variant="ghost" size="icon">
              <ArrowLeft className="h-4 w-4" />
            </Button>
          </Link>
          <div>
            <h1 className="text-3xl font-bold tracking-tight">My Watchlist</h1>
            <p className="text-muted-foreground">
              Track RFPs you're interested in as a vendor
            </p>
          </div>
        </div>
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
            <p className="mt-2 text-muted-foreground">Loading watchlist...</p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Link href="/request-for-proposal-marketplace">
            <Button variant="ghost" size="icon">
              <ArrowLeft className="h-4 w-4" />
            </Button>
          </Link>
          <div>
            <h1 className="text-3xl font-bold tracking-tight">My Watchlist</h1>
            <p className="text-muted-foreground">
              Track RFPs you're interested in as a vendor
            </p>
          </div>
        </div>
        {pagination && (
          <div className="text-sm text-muted-foreground">
            {pagination.total} RFPs in watchlist
          </div>
        )}
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Filter className="h-5 w-5" />
            <span>Filters</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">Search</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search RFPs..."
                  value={searchTerm}
                  onChange={(e) => handleSearch(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Status</label>
              <select
                value={statusFilter}
                onChange={(e) => handleStatusFilter(e.target.value)}
                className="w-full px-3 py-2 border border-input rounded-md bg-background"
              >
                <option value="all">All Statuses</option>
                <option value="PUBLISHED">Published</option>
                <option value="DRAFT">Draft</option>
                <option value="CLOSED">Closed</option>
                <option value="AWARDED">Awarded</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Category</label>
              <select
                value={categoryFilter}
                onChange={(e) => handleCategoryFilter(e.target.value)}
                className="w-full px-3 py-2 border border-input rounded-md bg-background"
              >
                <option value="all">All Categories</option>
                <optgroup label="Technology">
                  <option value="Technology">Technology</option>
                  <option value="Software Development">Software Development</option>
                  <option value="IT Services">IT Services</option>
                  <option value="Cybersecurity">Cybersecurity</option>
                  <option value="Cloud Services">Cloud Services</option>
                  <option value="Data Analytics">Data Analytics</option>
                  <option value="AI/ML">AI/ML</option>
                </optgroup>
                <optgroup label="Creative & Design">
                  <option value="Design">Design</option>
                  <option value="Graphic Design">Graphic Design</option>
                  <option value="Web Design">Web Design</option>
                  <option value="UI/UX Design">UI/UX Design</option>
                  <option value="Branding">Branding</option>
                  <option value="Video Production">Video Production</option>
                  <option value="Photography">Photography</option>
                </optgroup>
                <optgroup label="Marketing & Sales">
                  <option value="Marketing">Marketing</option>
                  <option value="Digital Marketing">Digital Marketing</option>
                  <option value="Content Marketing">Content Marketing</option>
                  <option value="SEO/SEM">SEO/SEM</option>
                  <option value="Social Media">Social Media</option>
                  <option value="Email Marketing">Email Marketing</option>
                  <option value="Sales">Sales</option>
                </optgroup>
                <optgroup label="Business Services">
                  <option value="Consulting">Consulting</option>
                  <option value="Legal Services">Legal Services</option>
                  <option value="Accounting">Accounting</option>
                  <option value="HR Services">HR Services</option>
                  <option value="Project Management">Project Management</option>
                  <option value="Training">Training</option>
                </optgroup>
                <optgroup label="Other">
                  <option value="Other">Other</option>
                  <option value="Healthcare">Healthcare</option>
                  <option value="Education">Education</option>
                  <option value="Real Estate">Real Estate</option>
                  <option value="Manufacturing">Manufacturing</option>
                  <option value="Logistics">Logistics</option>
                </optgroup>
              </select>
            </div>
          </div>
          <div className="flex justify-end mt-4">
            <Button
              variant="outline"
              onClick={clearFilters}
              className="text-sm"
            >
              Clear Filters
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Watchlist Items */}
      {watchlistItems.length === 0 ? (
        <Card>
          <CardContent className="text-center py-12">
            <FileText className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-medium mb-2">No RFPs in watchlist</h3>
            <p className="text-muted-foreground mb-4">
              Start adding RFPs to your watchlist to track them as a vendor
            </p>
            <Link href="/request-for-proposal-marketplace">
              <Button>
                Browse RFPs
              </Button>
            </Link>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {watchlistItems.map((item) => (
            <Card key={item.id} className="hover:shadow-md transition-shadow">
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0 overflow-hidden">
                    <div className="flex items-center space-x-2 mb-2">
                      <Link href={`/rfp/${item.rfp.id}`} className="hover:underline min-w-0 flex-1">
                        <h3 className="text-lg font-semibold truncate text-primary hover:text-primary/80 transition-colors">
                          {item.rfp.title}
                        </h3>
                      </Link>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(item.rfp.status)}`}>
                        {item.rfp.status.toLowerCase()}
                      </span>
                      <span className="px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                        {item.rfp.category}
                      </span>
                    </div>
                    
                                          <p className="text-muted-foreground mb-4 line-clamp-2 overflow-hidden text-ellipsis break-words">
                      {item.rfp.description}
                    </p>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                      <div className="flex items-center space-x-2">
                        <DollarSign className="h-4 w-4 text-muted-foreground" />
                        <span className="text-sm">
                          {formatCurrency(item.rfp.budgetMin, item.rfp.budgetCurrency)} - {formatCurrency(item.rfp.budgetMax, item.rfp.budgetCurrency)}
                        </span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Calendar className="h-4 w-4 text-muted-foreground" />
                        <span className="text-sm">Due {formatDate(item.rfp.deadline)}</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Eye className="h-4 w-4 text-muted-foreground" />
                        <span className="text-sm">{item.rfp.views} views</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <MessageSquare className="h-4 w-4 text-muted-foreground" />
                        <span className="text-sm">{item.rfp.responses} responses</span>
                      </div>
                    </div>

                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2 text-sm text-muted-foreground">
                        <span>Posted by {item.rfp.user.name}</span>
                        <span>•</span>
                        <span>{item.rfp.user.company}</span>
                        <span>•</span>
                        <span>Added {formatDate(item.addedAt)}</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Link href={`/rfp/${item.rfp.id}`}>
                          <Button variant="outline" size="sm">
                            View Details
                          </Button>
                        </Link>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => removeFromWatchlist(item.rfp.id)}
                          className="text-red-600 hover:text-red-700"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Pagination */}
      {pagination && pagination.totalPages > 1 && (
        <div className="flex items-center justify-between">
          <div className="text-sm text-muted-foreground">
            Showing {((pagination.page - 1) * pagination.limit) + 1} to {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} RFPs
          </div>
          <div className="flex items-center space-x-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(pagination.page - 1)}
              disabled={!pagination.hasPrev}
            >
              <ArrowLeft className="h-4 w-4" />
              Previous
            </Button>
            <span className="text-sm">
              Page {pagination.page} of {pagination.totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(pagination.page + 1)}
              disabled={!pagination.hasNext}
            >
              Next
              <ArrowRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  )
} 